import {Observable, of} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum
} from '@spa/api';

export class ErrorsHelper {
    private constructor() {

    }

    static createErrorResponseObservable<DataType>(
        response: ApiResponse<ApiError[] | DataType>,
        errorState?: number | number[],
    ): Observable<ApiResponse<ApiError[]>> {
        let errorData = [];

        const errorStateType = typeof errorState;

        if (errorStateType !== 'undefined') {
            if (!Array.isArray(errorState)) {
                const apiError = new ApiError(errorState);

                errorData.push(apiError);
            } else {
                errorData = errorState.map((state: number) => new ApiError(state));
            }

        } else {
            errorData = response.data as ApiError[];
        }

        const errorResponse = new ApiResponse<ApiError[]>(response.status, errorData, response.messages);

        return of(errorResponse);
    }

    static createErrorResponse<DataType>(
        response: ApiResponse<ApiError[] | DataType>,
        errorState?: number | number[],
        errorData?: any
    ): ApiResponse<ApiError[]> {
        let errors = [];

        if (typeof errorState !== 'undefined') {
            if (!Array.isArray(errorState)) {
                const apiError = new ApiError(errorState, errorData);

                errors.push(apiError);
            } else {
                errors = errorState.map((state: number) => new ApiError(state, errorData));
            }

        } else {
            errors = response.data as ApiError[];
        }

        return new ApiResponse<ApiError[]>(response.status, errors, response.messages);
    }

    static createReducedErrorResponse<DataType>(
        responses: ApiResponse<ApiError[] | DataType>[],
        errorState?: number
    ): ApiResponse<ApiError[]> {
        if (typeof errorState === 'undefined') {
            return responses
                .reduce((reducedResponse: ApiResponse<ApiError[]>, response: ApiResponse<ApiError[] | DataType>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        reducedResponse.data.push(...response.data as ApiError[]);
                        reducedResponse.messages.push(...response.messages);
                    }

                    return reducedResponse;
                }, new ApiResponse(HttpStatusCodesEnum.InternalServerError, [], [])) as ApiResponse<ApiError[]>;
        }

        const initialResponse =
            new ApiResponse(HttpStatusCodesEnum.InternalServerError, [new ApiError(errorState)], []);

        return responses
            .reduce((reducedResponse: ApiResponse<ApiError[]>, response: ApiResponse<ApiError[] | DataType>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    reducedResponse.messages.push(...response.messages);
                }

                return reducedResponse;
            }, initialResponse) as ApiResponse<ApiError[]>;
    }

    static createReducedErrorResponseObservable<DataType>(
        responses: ApiResponse<ApiError[] | DataType>[],
        errorState?: number
    ): Observable<ApiResponse<ApiError[]>> {
        let errorResponse = null;

        if (typeof errorState !== 'undefined') {
            const initialResponse =
                new ApiResponse(HttpStatusCodesEnum.InternalServerError, [new ApiError(errorState)], []);

            errorResponse = responses
                .reduce((reducedResponse: ApiResponse<ApiError[]>, response: ApiResponse<ApiError[] | DataType>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        reducedResponse.messages.push(...response.messages);
                    }

                    return reducedResponse;
                }, initialResponse);
        } else {
            errorResponse = responses
                .reduce((reducedResponse: ApiResponse<ApiError[]>, response: ApiResponse<ApiError[] | DataType>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        reducedResponse.data.push(...response.data as ApiError[]);
                        reducedResponse.messages.push(...response.messages);
                    }

                    return reducedResponse;
                }, new ApiResponse(HttpStatusCodesEnum.InternalServerError, [], [])) as ApiResponse<ApiError[]>;
        }

        return of(errorResponse);
    }

    static checkForErrorResponses<DataType>(responses: ApiResponse<ApiError[] | DataType>[]): boolean {
        return responses.map((response: ApiResponse<ApiError[] | DataType>) => {
            return response.status !== HttpStatusCodesEnum.Ok;
        }).indexOf(true) >= 0;
    }

    static createResponseForInvalidData(errorState?: number): ApiResponse<ApiError[]> {
        const error = new ApiError();

        error.status = !errorState ? HttpStatusCodesEnum.BadRequest : errorState;
        error.data = 'Invalid parameters';

        return new ApiResponse<ApiError[]>(HttpStatusCodesEnum.BadRequest, [error], ['Invalid parameters']);
    }

    static createResponseForNoData(errorState?: number): ApiResponse<ApiError[]> {
        const error = new ApiError();

        error.data = 'Not Found';
        error.status = !errorState ? HttpStatusCodesEnum.NotFound : errorState;

        return new ApiResponse(HttpStatusCodesEnum.NotFound, [error], ['Not Found']);
    }

    static createBadRequestResponse(errorState?: number): ApiResponse<ApiError[]> {
        const error = new ApiError();

        error.data = 'Bad Request';
        error.status = !errorState ? HttpStatusCodesEnum.BadRequest : errorState;

        return new ApiResponse(HttpStatusCodesEnum.BadRequest, [error], ['Bad Request']);
    }

    static createBadRequestResponseObservable(errorState?: number): Observable<ApiResponse<ApiError[]>> {
        const error = new ApiError();

        error.data = 'Bad Request';
        error.status = !errorState ? HttpStatusCodesEnum.BadRequest : errorState;

        const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error], ['Bad Request']);

        return of(errorResponse);
    }

    static createBadRequestMessageResponseObservable(errorMessage: string, errorState?: number): Observable<ApiResponse<ApiError[]>> {
        const error = new ApiError();

        error.data = errorMessage;
        error.status = !errorState ? HttpStatusCodesEnum.BadRequest : errorState;

        const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error], [errorMessage]);

        return of(errorResponse);
    }

    static checkForInvalidRespondedObject<DataType>(response: ApiResponse<ApiError[] | DataType>): boolean {
        return (response.data === null) ||
            (typeof response.data !== 'object') ||
            (Object.keys(response.data).length === 0);
    }

    static checkForInvalidRespondedArray<DataType>(response: ApiResponse<ApiError[] | DataType[]>): boolean {
        return !Array.isArray(response.data) ||
            ((Array.isArray(response.data) && response.data.length === 0));
    }
}
